import {
  Box,
  Button,
  CustomModalLayout,
  Heading,
  IconButton,
  ModalMobileLayout,
  Text,
  TextButton,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FunctionComponent, useContext } from 'react';
import { DATA_HOOKS } from '../../../dataHooks';
import css from './index.module.scss';
import { ExternalLinkSmall, ArrowLeftSmall } from 'wix-ui-icons-common';
import {
  Article,
  ArticleResolution,
  FeatureRequestResolutions,
} from '@wix/answers-api';
import { stripHTML } from '../../../utils/text';
import { useBI } from '../../../hooks/useBI';
import { BI, TOOLTIP_ENTER_DELAY } from '../../../constants';
import {
  tabToName,
  launchedTagText,
  statusToClass,
} from '../../../utils/roadmap';
import { useRouter } from 'next/router';
import { roadmapWishlistFormClick } from '@wix/bi-logger-new-help-center/v2';
import { isMobile } from '../../../utils/userAgent';
import { Context } from '../../../context';
import { RoadmapResolutionBadge } from '../../RoadmapResolutionBadge';

export type RoadmapFeatureRequestSuggestionModalProps = {
  article: Article;
  onSend: () => Promise<void>;
  onVote: (articleId: string) => void;
  onClose: () => void;
  onBack: () => void;
};
export const RoadmapFeatureRequestSuggestionModal: FunctionComponent<
  RoadmapFeatureRequestSuggestionModalProps
> = ({ article, onSend, onVote, onClose, onBack }) => {
  const { t } = useTranslation();
  const { sendBIEvent } = useBI();
  const { locale } = useRouter();
  const context = useContext(Context);
  const isMobileView = isMobile(context);

  const onClickBack = async () => {
    await sendBIEvent(
      roadmapWishlistFormClick({
        form_step: BI.WISHLIST_FORM.ALTERNATIVE_SUGGESTER,
        source_name: BI.SOURCE_NAMES.ROADMAP,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.BACK,
        kb_lang: locale as string,
        chosen_alternative_resolution: article.resolution
          ? tabToName(article.resolution)
          : undefined,
        chosen_alternative_id: article.id,
      })
    );
    onBack();
  };
  const articleContent = stripHTML(article.content);

  const resolution =
    statusToClass[
      (article.resolution as unknown as FeatureRequestResolutions) ||
        ArticleResolution.COLLECTING_VOTES
    ];

  const modalBody = (
    <Box direction="vertical" gap={isMobileView ? 'SP1' : 'SP3'}>
      <Text size="medium" weight="bold">
        {article.resolution === ArticleResolution.COLLECTING_VOTES
          ? t('feature-request.suggestion.modal.question.vote')
          : t('feature-request.suggestion.modal.question.follow')}
      </Text>
      <Box
        padding={isMobileView ? 'SP2' : 'SP4'}
        className={css.articleSuggestion}
        direction="vertical"
      >
        {article.resolution ? (
          isMobileView ? (
            <div className={`${css.statusBody}`}>
              <Text
                dataHook={DATA_HOOKS.ARTICLE_ITEM_STATUS}
                datatype={`${article.resolution}`}
                size="small"
                weight="thin"
                className={`${css.statusText} ${css[resolution]}`}
              >
                {article.resolution === ArticleResolution.RELEASED &&
                article.resolutionEta
                  ? launchedTagText(article.resolutionEta, t)
                  : t(`roadmap.article-list.resolutions.${article.resolution}`)}
              </Text>
            </div>
          ) : (
            <RoadmapResolutionBadge
              resolution={article.resolution}
              showTooltip={true}
              size="tiny"
            />
          )
        ) : null}
        <Text className={css.articleSuggestionTitle} size="small" weight="bold">
          {article.title}
        </Text>
        <Text
          weight="thin"
          maxLines={2}
          ellipsis={true}
          dataHook={DATA_HOOKS.ARTICLE_ITEM_CONTENT}
          className={`${css.articleContent}`}
          size="tiny"
          tooltipProps={{ enterDelay: TOOLTIP_ENTER_DELAY }}
          showTooltip={false}
        >
          {articleContent}
        </Text>
        <TextButton
          className={css.readMoreButton}
          weight="thin"
          size="tiny"
          suffixIcon={<ExternalLinkSmall />}
          as="a"
          href={article.uri}
          target="_blank"
        >
          {t('feature-request.suggestion.modal.read-more')}
        </TextButton>
      </Box>
    </Box>
  );

  const onVoteArticle = () => onVote(article.id);

  const mobileFooter = (
    <Box direction="horizontal" align="space-between">
      <IconButton priority="secondary" size="tiny" onClick={onBack}>
        <ArrowLeftSmall />
      </IconButton>
      <Box gap="SP2" direction="horizontal" align="right">
        <Button size="tiny" priority="secondary" onClick={onSend}>
          {t('feature-request.suggestion.modal.send')}
        </Button>
        <Button size="tiny" onClick={onVoteArticle}>
          {article.resolution === ArticleResolution.COLLECTING_VOTES
            ? t('feature-request.suggestion.modal.vote')
            : t('feature-request.suggestion.modal.follow')}
        </Button>
      </Box>
    </Box>
  );

  return isMobile(context) ? (
    <ModalMobileLayout
      className={css.mobileSuggestionModal}
      title={
        <Box direction="vertical">
          <Heading className={css.articleSuggestionModalTitle} appearance="H2">
            {t('feature-request.suggestion.modal.title')}
          </Heading>
          <Text weight="thin">
            {t('feature-request.suggestion.modal.subtitle')}
          </Text>
        </Box>
      }
      footer={mobileFooter}
      content={modalBody}
      onCloseButtonClick={onClose}
      dataHook={DATA_HOOKS.ROADMAP_FEATURE_REQUEST_MODAL_BODY}
    />
  ) : (
    <CustomModalLayout
      secondaryButtonText={t('feature-request.suggestion.modal.send')}
      primaryButtonText={
        article.resolution === ArticleResolution.COLLECTING_VOTES
          ? t('feature-request.suggestion.modal.vote')
          : t('feature-request.suggestion.modal.follow')
      }
      secondaryButtonOnClick={onSend}
      primaryButtonOnClick={onVoteArticle}
      onCloseButtonClick={onClose}
      sideActions={
        <Button
          onClick={onClickBack}
          dataHook={
            DATA_HOOKS.ROADMAP_FEATURE_REQUEST_SUGGESTION_MODAL_BACK_BUTTON
          }
          priority="secondary"
          size="small"
          prefixIcon={<ArrowLeftSmall />}
        >
          {t('feature-request.suggestion.modal.back')}
        </Button>
      }
      title={t('feature-request.suggestion.modal.title')}
      subtitle={t('feature-request.suggestion.modal.subtitle')}
      content={modalBody}
      width={'600px'}
      dataHook={DATA_HOOKS.ROADMAP_FEATURE_REQUEST_SUGGESTION_MODAL_BODY}
    />
  );
};
